import React from 'react';
import { ButtonWithLoading } from '@spark/ui-components/components/Button';
import Badge from '@spark/ui-components/components/Badge';
import Icon from '@spark/ui-components/components/Icon';
import { notification } from '@spark/ui-components/utils/notification';
import TruncatedText from '@spark/ui-components/components/TanStackTable/components/TruncatedText';

const STATUS_ATUAL_BADGE_MAP = {
  '1': { label: 'Em aberto', bg: 'danger' },
  '2': { label: 'Visto', bg: 'primary' },
  '3': { label: 'Em analise', bg: 'warning' },
  '4': { label: 'Em comite', bg: 'info' },
  '5': { label: 'Finalizado', bg: 'success' },
  emaberto: { label: 'Em aberto', bg: 'danger' },
  visto: { label: 'Visto', bg: 'primary' },
  emanalise: { label: 'Em analise', bg: 'warning' },
  emcomite: { label: 'Em comite', bg: 'info' },
  finalizado: { label: 'Finalizado', bg: 'success' },
};

const normalizeStatusKey = value =>
  String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9]/g, '')
    .toLowerCase();

const getStatusBadge = value => {
  if (value === undefined || value === null || String(value).trim() === '') {
    return null;
  }

  const raw = String(value).trim();
  const normalized = normalizeStatusKey(raw);
  const mapped = STATUS_ATUAL_BADGE_MAP[raw] || STATUS_ATUAL_BADGE_MAP[normalized];

  if (mapped) {
    return mapped;
  }

  return {
    label: raw,
    bg: 'secondary',
  };
};

const copyText = async text => {
  if (navigator?.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }

  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.style.position = 'fixed';
  textarea.style.opacity = '0';
  document.body.appendChild(textarea);
  textarea.focus();
  textarea.select();
  document.execCommand('copy');
  document.body.removeChild(textarea);
};

export const getOuvidoriaColumns = ({ onEdit, onDelete } = {}) => [
  {
    id: 'protocolo',
    header: 'Protocolo',
    accessorKey: 'protocolo',
    size: 180,
    enableSorting: true,
    cell: ({ getValue }) => {
      const protocolo = String(getValue() || '').trim();

      return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          {protocolo && (
            <button
              type="button"
              title="Copiar protocolo"
              aria-label="Copiar protocolo"
              onClick={async event => {
                event.stopPropagation();
                try {
                  await copyText(protocolo);
                  notification.success('Protocolo copiado para a area de transferencia.');
                } catch (error) {
                  console.error('Erro ao copiar protocolo:', error);
                  notification.error('Nao foi possivel copiar o protocolo.');
                }
              }}
              style={{
                border: 'none',
                background: 'transparent',
                padding: 0,
                display: 'inline-flex',
                alignItems: 'center',
                cursor: 'pointer',
                color: '#4b5563',
              }}
            >
              <Icon name="content-copy" size={16} />
            </button>
          )}
          <TruncatedText value={protocolo} maxLength={32} />
        </div>
      );
    },
  },
  {
    id: 'motivoNome',
    header: 'Motivo',
    accessorKey: 'motivoNome',
    size: 180,
    enableSorting: true,
    cell: ({ getValue }) => <TruncatedText value={getValue()} maxLength={48} />,
  },
  {
    id: 'relato',
    header: 'Relato',
    accessorKey: 'relato',
    size: 320,
    enableSorting: false,
    cell: ({ getValue }) => <TruncatedText value={getValue()} maxLength={120} />,
  },
  {
    id: 'statusAtual',
    header: 'Status atual',
    accessorKey: 'statusAtual',
    size: 150,
    enableSorting: true,
    cell: ({ getValue }) => {
      const status = getStatusBadge(getValue());

      if (!status) {
        return <span>-</span>;
      }

      return <Badge bg={status.bg}>{status.label}</Badge>;
    },
  },
  {
    id: 'desejaAcompanharLabel',
    header: 'Acompanhar',
    accessorKey: 'desejaAcompanharLabel',
    size: 120,
    enableSorting: true,
    cell: ({ getValue }) => <span>{getValue() || '-'}</span>,
  },
  {
    id: 'relatadoLiderStatus',
    header: 'Relatado lider',
    accessorKey: 'relatadoLiderStatus',
    size: 150,
    enableSorting: true,
    cell: ({ getValue }) => <span>{getValue() || '-'}</span>,
  },
  {
    id: 'tagsLabel',
    header: 'Tags',
    accessorKey: 'tagsLabel',
    size: 220,
    enableSorting: false,
    cell: ({ getValue }) => <TruncatedText value={getValue()} maxLength={80} />,
  },
  {
    id: 'dataCriacaoFormatada',
    header: 'Data de criacao',
    accessorKey: 'dataCriacaoFormatada',
    size: 180,
    enableSorting: true,
    cell: ({ getValue }) => <span>{getValue() || '-'}</span>,
  },
  ...(onEdit || onDelete
    ? [
        {
          id: 'actions',
          header: 'Acoes',
          size: 160,
          enableSorting: false,
          cell: ({ row }) => (
            <div style={{ display: 'flex', gap: '6px' }}>
              {onEdit && (
                <ButtonWithLoading
                  size="sm"
                  variant="outline-primary"
                  onClick={event => {
                    event.stopPropagation();
                    onEdit(row.original.id);
                  }}
                >
                  Detalhes
                </ButtonWithLoading>
              )}
            </div>
          ),
        },
      ]
    : []),
];
